/* eslint-disable jsx-a11y/anchor-is-valid */
import ApexCharts, {ApexOptions} from 'apexcharts'
import dayjs from 'dayjs'
import {envImagePath, hdmImageBackHost} from 'GLOBAL/envVariables'
import _ from 'lodash'
import React, {useEffect, useRef} from 'react'
import {getCSS, getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

type Props = {
  className?: string
  data?: any
  date?: any
  filter?: any
  value?: any
  imageList?: any
  useChart?: any
  selectedOption?: any
  onDataClick?: any
  chartHeight?: number
}

const CustomerDetailChartFix: React.FC<Props> = ({
  className,
  useChart,
  filter,
  data,
  date,
  imageList,
  selectedOption,
  onDataClick,
  chartHeight,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)

  const {mode} = useThemeMode()
  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, data, date, useChart, imageList, onDataClick, selectedOption)
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, data, date, filter, useChart, imageList, selectedOption])

  return (
    <div className={`${className}`}>
      {/* begin::Header */}
      <div className='d-flex pt-4 border-0 p-0'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='text-muted fw-semibold fs-7'>
            검침내역을 차트로 한눈에 볼 수 있습니다.
          </span>
        </h3>
      </div>

      <div className='card-body p-0'>
        <div ref={chartRef} id='kt_charts_widget_4_chart' style={{height: chartHeight ?? 494}}></div>
      </div>
    </div>
  )
}

export {CustomerDetailChartFix}

function getChartOptions(
  height: number,
  data: any,
  date: any,
  useChart: any,
  imageList: any,
  onDataClick: any,
  selectedOption: any
): ApexOptions {
  const labelColor = getCSSVariableValue('--kt-gray-800')
  const borderColor = getCSSVariableValue('--kt-gray-400')
  const fontSize = '14px'

  const toNum = (v: any): number | null => {
    if (v === null || v === undefined) return null
    const s = String(v).trim()
    if (s === '') return null
    const n = Number(s.replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  const getValue = (d: any, camelKey: string, snakeKey: string) => {
    return d?.[camelKey] ?? d?.[snakeKey]
  }

  const isBadForChart = (d: any) => {
    const processStatus = getValue(d, 'processStatus', 'process_status')
    const checkValue = getValue(d, 'checkValue', 'check_value')

    if (['FAIL', 'IMGERR'].includes(processStatus)) return true
    return toNum(checkValue) === null
  }

  const xData: any = (() => {
    // 1. 하루 단위로 묶고, 필요한 데이터 필터링
    const allFilteredData = _.chain(imageList)
      .groupBy((el) => {
        const idxDate = getValue(el, 'idxDate', 'idx_date')
        const idxDateStr = idxDate ? String(idxDate) : ''
        return idxDateStr.slice(0, -4)
      })
      .flatMap((groupedData, baseDate) => {
        const sortedData = groupedData.sort((a, b) => {
          const aIdxDate = String(getValue(a, 'idxDate', 'idx_date') ?? '')
          const bIdxDate = String(getValue(b, 'idxDate', 'idx_date') ?? '')
          return aIdxDate.localeCompare(bIdxDate)
        })

        const now = dayjs()
        let monthDiff = 0
        if (date.length >= 2) {
          const [start, end] = date
          if (start && end) {
            monthDiff = Math.abs(end.diff(start, 'month'))
          } else {
            monthDiff = -1
          }
        } else if (date.length === 1 && date[0]) {
          monthDiff = Math.abs(now.diff(date[0], 'month'))
        } else {
          monthDiff = -1
        }

        let selected: typeof groupedData = []

        if (monthDiff === -1 || monthDiff >= 2) {
          // 2달 이상이면
          selected = sortedData.find((d) => !isBadForChart(d))
            ? [sortedData.find((d) => !isBadForChart(d))!]
            : []
        } else if (monthDiff >= 1) {
          // 1달 ~ 2달 → 홀수 데이터
          selected = sortedData
            .filter((d) => !isBadForChart(d))
            .filter((d) => {
              const idxDate = String(getValue(d, 'idxDate', 'idx_date') ?? '')
              const idx = parseInt(idxDate.slice(-4))
              return idx % 2 === 1
            })
        } else {
          // 1달 미만 → 전부 사용
          selected = sortedData.filter((d) => !isBadForChart(d))
        }

        return selected
      })
      .filter(Boolean)
      .value()

    // 2. 전체를 시간순 정렬
    allFilteredData.sort((a, b) => {
      const aCreateAt = getValue(a, 'createAt', 'create_at')
      const bCreateAt = getValue(b, 'createAt', 'create_at')

      return dayjs(aCreateAt).valueOf() - dayjs(bCreateAt).valueOf()
    })

    // 3. 최종 xData 만들기
    if (selectedOption === 'reading') {
      return allFilteredData.map((data) => ({
        x: dayjs(getValue(data, 'createAt', 'create_at')).toDate(),
        y: toNum(getValue(data, 'checkValue', 'check_value'))!,
        goals: getValue(data, 'filePath', 'file_path'),
        rotate: data.rotate,
      }))
    } else if (selectedOption === 'usage') {
      const usageList = []

      if (allFilteredData.length > 0) {
        const first = allFilteredData[0]

        usageList.push({
          x: dayjs(getValue(first, 'createAt', 'create_at')).toDate(),
          y: 0,
          goals: getValue(first, 'filePath', 'file_path'),
          rotate: first.rotate,
        })
      }

      for (let i = 1; i < allFilteredData.length; i++) {
        const prev = allFilteredData[i - 1]
        const curr = allFilteredData[i]

        const prevV = toNum(getValue(prev, 'checkValue', 'check_value'))
        const currV = toNum(getValue(curr, 'checkValue', 'check_value'))

        if (prevV === null || currV === null) continue

        const usage = currV - prevV

        usageList.push({
          x: dayjs(getValue(curr, 'createAt', 'create_at')).toDate(),
          y: usage,
          goals: getValue(curr, 'filePath', 'file_path'),
          rotate: curr.rotate,
        })
      }

      return usageList
    }

    return []
  })()

  console.log('xData source file path:', {
    idxDate: getValue(data, 'idxDate', 'idx_date'),
    filePath: getValue(data, 'filePath', 'file_path'),
    raw: data,
  })

  const minY = Math.min(...xData.map((d: any) => d.y))
  const maxY = Math.max(...xData.map((d: any) => d.y))

  let adjustedMin = Math.floor(minY * 0.9998)
  let adjustedMax = Math.ceil(maxY * 1.0002)

  if (minY === maxY) {
    adjustedMin = minY - 2 < 0 ? 0 : minY - 2
    adjustedMax = maxY + 2
  }

  const firstDate = dayjs(xData[0]?.x)
  const lastDate = dayjs(xData[xData.length - 1]?.x)

  const diffInMonths = lastDate.diff(firstDate, 'month', true)
  const diffInYears = lastDate.diff(firstDate, 'year', true)

  let xaxisFormat = 'yyyy년 MM월 dd일' // 디폴트
  if (diffInYears >= 1) {
    xaxisFormat = 'yyyy년 MM월' // 연도가 바뀌면 월까지만
  } else if (diffInMonths >= 1) {
    xaxisFormat = 'MM월 dd일' // 월이 바뀌면 월-일
  } else {
    xaxisFormat = 'MM월 dd일' // 같은 달 안에서는 일만
  }

  const unitBySelectedOptions = (option: any) => {
    switch (option) {
      case 'usage':
        return 't'
      case 'reading':
        return 'm³'
      default:
        return 'm³'
    }
  }

  return {
    series: [
      {
        name: '검침 데이터',
        data: xData,
      },
    ],
    chart: {
      type: 'line',
      height: height,
      toolbar: {
        show: true,
        offsetY: 10,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: true,
        },
      },
    },
    legend: {
      show: true,
      fontSize: fontSize,
      fontWeight: 600,
    },

    fill: {
      opacity: 1,
    },

    stroke: {
      width: 2,
    },
    markers: {
      size: 0,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        format: xaxisFormat,
        style: {
          colors: labelColor,
          fontSize: '16px',
          fontWeight: 600,
        },
        rotate: 0,
        hideOverlappingLabels: true,
        showDuplicates: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    yaxis: {
      forceNiceScale: true,
      min: adjustedMin,
      max: adjustedMax,
      labels: {
        style: {
          colors: labelColor,
          fontSize: fontSize,
          fontWeight: 600,
        },
        offsetX: 0,
        formatter(val: number): string {
          return (
            Math.round(val).toLocaleString('ko-KR') + `${unitBySelectedOptions(selectedOption)}`
          )
        },
      },
    },

    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: fontSize,
      },
      custom: function ({series, seriesIndex, dataPointIndex, w}) {
        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex]

        const deviceType = getValue(data, 'deviceType', 'device_type')

        const xValue = dayjs(dataPoint.x).format('MM월 DD일 HH시')
        const rotate = dataPoint.rotate || 0
        const usageOrReading = selectedOption === 'reading' ? '검침값' : '사용량'

        let image = dataPoint.goals
        let displayYValue: any = dataPoint.y

        const getHdmReadingValue = (item: any) => {
          const checkValue = getValue(item, 'checkValue', 'check_value')
          const checkDecimalValue = getValue(item, 'checkDecimalValue', 'check_decimal_value')

          if (checkValue === null || checkValue === undefined || checkValue === '') {
            return dataPoint.y
          }

          if (
            checkDecimalValue === null ||
            checkDecimalValue === undefined ||
            checkDecimalValue === ''
          ) {
            return checkValue
          }

          const decimalText = String(checkDecimalValue)

          if (decimalText.includes('.')) {
            return `${checkValue}.${decimalText.split('.')[1]}`
          }

          return `${checkValue}.${decimalText}`
        }

        if (deviceType === 'hdm') {
          const matchedImageItem = imageList?.find((item: any) => {
            const itemCreateAt = dayjs(getValue(item, 'createAt', 'create_at')).valueOf()
            const pointCreateAt = dayjs(dataPoint.x).valueOf()

            const itemCheckValue = toNum(getValue(item, 'checkValue', 'check_value'))
            const pointCheckValue = toNum(dataPoint.y)

            return itemCreateAt === pointCreateAt && itemCheckValue === pointCheckValue
          })

          image = getValue(matchedImageItem, 'filePath', 'file_path')

          if (selectedOption === 'reading') {
            displayYValue = getHdmReadingValue(matchedImageItem)
          }
        }

        const formattedYValue =
          selectedOption === 'usage'
            ? Number(displayYValue).toLocaleString('ko-KR')
            : String(displayYValue)

        const imageURL =
          image && deviceType === 'hdm'
            ? `${hdmImageBackHost}/api/select${String(image).startsWith('/') ? image : `/${image}`}`
            : image
            ? `${envImagePath}${String(image).startsWith('/') ? image : `/${image}`}`
            : ''

        const adjustedRotate =
          (rotate > 90 && rotate < 270) || (rotate < -90 && rotate > -270) ? rotate : 0

        const renderHeader = () => `
        <div class="card-header row align-items-center font-semibold text-[1.375rem]" id="tooltip-${xValue}">
          <h2 class="mt-2">${xValue}</h2>
          <h2>${usageOrReading} : <u>${formattedYValue}${unitBySelectedOptions(
          selectedOption
        )}</u></h2>
        </div>
          `

        if (deviceType === 'analog' || deviceType === 'digital') {
          if (!image) {
            return `
            <div class="card" onclick="handleTooltipClick()">
              ${renderHeader()}
            </div>
          `
          }

          return `
          <div class="card" onclick="handleTooltipClick()">
            ${renderHeader()}
            <div class="card-body p-0">
              <img 
                src="${imageURL}" 
                style="max-width: 100%; max-height: 250px; object-fit: contain; transform: rotate(${adjustedRotate}deg);"
              />
            </div>
          </div>
          `
        } else if (deviceType === 'hdm') {
          if (!image) {
            return `
            <div class="card" onclick="handleTooltipClick()">
              ${renderHeader()}
            </div>
          `
          }

          return `
          <div class="card" onclick="handleTooltipClick()">
            ${renderHeader()}
            <div class="card-body p-0">
              <img 
                src="${imageURL}" 
                style="max-width: 100%; max-height: 250px; object-fit: contain; transform: rotate(${adjustedRotate}deg);"
              />
            </div>
          </div>
          `
        }

        return `
          <div class="card" onclick="handleTooltipClick()">
          ${renderHeader()}
          </div>
          `
      },

      x: {
        formatter(val, opts?) {
          return ''
        },
      },
      y: {
        formatter: function (val, opts) {
          return Number(val).toLocaleString('ko-KR') + `${unitBySelectedOptions(selectedOption)}`
        },
      },
    },

    colors: ['#068029', '#15dc4e'],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    annotations:
      selectedOption === 'usage'
        ? {
            yaxis: [
              {
                y: 0,
                y2: -10000000,
                borderColor: '#FF0000',
                fillColor: '#FFE5E5',
                opacity: 0.4,
                label: {
                  style: {
                    color: '#FF0000',
                    background: 'transparent',
                  },
                },
              },
            ],
          }
        : undefined,
  }
}
